import { useEffect, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import {
  DEFAULT_PROGRAM_AUDIO,
  isLayoutMessage,
  type LayerTree,
  type ProgramAudio,
  type RequestStateMessage,
} from '@liveforge/shared';

export interface ChannelParams {
  liveId: string;
  apiUrl: string;
}

/**
 * Receives the layer tree from the host, through the backend relay.
 *
 * Callers connect as a **viewer**: no credential at all, because the headless
 * Chrome that loads the Layout Page has none to offer and a guest has no
 * account. The relay admits a viewer read-only, so the worst a stranger who
 * guesses a Live id can do is watch a composition.
 *
 * `params` does **not** have to be memoised. It used to, and the requirement
 * was a trap rather than a contract: this hook sets state on connect, so a
 * caller passing a fresh object closed the socket it had just opened, set
 * `connected` false, re-rendered, and opened another - a reconnect loop that
 * never settles and never reports a fault, because every disconnect is the
 * caller's own `close()`. The Layout Page did exactly that and could not have
 * worked inside Egress. The effect keys on the values instead.
 */
export function useLayoutChannel(params: ChannelParams | undefined) {
  const [layers, setLayers] = useState<LayerTree | null>(null);
  const [audio, setAudio] = useState<ProgramAudio>(DEFAULT_PROGRAM_AUDIO);
  const [connected, setConnected] = useState(false);
  /**
   * Why the channel is down, when it is.
   *
   * A compositing page that cannot reach the relay renders a black frame, and
   * without this it renders a black frame for every possible reason at once.
   * The equivalent silence in the studio cost an hour on 2026-08-19.
   */
  const [error, setError] = useState<string | null>(null);

  // Read out before the effect so it depends on two strings rather than on
  // the identity of the object holding them.
  const liveId = params?.liveId;
  const apiUrl = params?.apiUrl;

  useEffect(() => {
    if (!liveId || apiUrl === undefined) return;

    const socket: Socket = io(apiUrl, {
      path: '/socket.io',
      auth: { liveId, role: 'viewer' },
      transports: ['websocket', 'polling'],
    });

    // The host only sends on change. A page that connected after the last edit
    // would render an empty canvas until the host happened to touch something,
    // so it asks for the current tree rather than waiting.
    socket.on('connect', () => {
      setConnected(true);
      setError(null);
      const request: RequestStateMessage = { type: 'request_state', liveId };
      socket.emit('layout', request);
    });

    socket.on('disconnect', (reason: string) => {
      setConnected(false);
      setError(`Disconnected from the layout channel: ${reason}`);
    });

    socket.on('connect_error', (cause: Error) => {
      setConnected(false);
      setError(cause.message);
    });

    socket.on('layout', (payload: unknown) => {
      if (isLayoutMessage(payload) && payload.type === 'state_update') {
        setLayers(payload.state.layers);
        // A host on an older build sends no mix; everyone stays audible rather
        // than the broadcast falling silent.
        setAudio(payload.state.audio ?? DEFAULT_PROGRAM_AUDIO);
      }
    });

    return () => {
      socket.close();
    };
  }, [liveId, apiUrl]);

  return { layers, audio, connected, error };
}
